import { Switch, Typography } from 'antd';
import { CheckCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface Props {
  enabled: boolean;
  loading: boolean;
  onToggle: (enabled: boolean) => void;
}

export default function AvailabilityCard({ enabled, loading, onToggle }: Props) {
  const { t } = useTranslation('provider');

  const accent = enabled ? '#1f8b4c' : '#fa8c16';
  const accentDark = enabled ? '#27ae60' : '#d46b08';

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 18,
        background: `linear-gradient(135deg, ${accent}10 0%, ${accent}1c 60%, ${accent}08 100%)`,
        border: `1px solid ${accent}24`,
        padding: '14px 16px',
        marginBottom: 12,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        overflow: 'hidden',
        boxShadow: `0 4px 14px ${accent}10`,
      }}
    >
      {/* Decorative blur orb */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -30,
          right: -25,
          width: 110,
          height: 110,
          borderRadius: '50%',
          background: `${accent}24`,
          filter: 'blur(6px)',
          opacity: 0.55,
        }}
      />

      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
        {/* Icon disc with pulse for active */}
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            flexShrink: 0,
            background: `linear-gradient(135deg, ${accent} 0%, ${accentDark} 100%)`,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 19,
            boxShadow: `0 4px 12px ${accent}45, inset 0 1px 0 rgba(255,255,255,0.22)`,
          }}
        >
          {enabled ? <CheckCircleOutlined /> : <PauseCircleOutlined />}
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <Text
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--bl-text-strong, #0f172a)',
                letterSpacing: -0.1,
              }}
            >
              {t('schedule.availability.title')}
            </Text>
            {enabled && (
              <span
                aria-hidden
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: accent,
                  boxShadow: `0 0 0 4px ${accent}28`,
                  display: 'inline-block',
                }}
              />
            )}
          </div>
          <Text
            style={{
              fontSize: 12,
              color: 'var(--bl-text-muted, #6b7280)',
              lineHeight: 1.4,
            }}
          >
            {enabled
              ? t('schedule.availability.onSubtitle')
              : t('schedule.availability.offSubtitle')}
          </Text>
        </div>
      </div>

      <Switch
        checked={enabled}
        onChange={onToggle}
        loading={loading}
        style={{ flexShrink: 0 }}
      />
    </div>
  );
}
